import {Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {SmOverflowTooltipComponent} from '../sm-overflow-tooltip/sm-overflow-tooltip.component';

export type SmActionButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'warning';
export type SmActionButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'tile';

const TOUCH_TOOLTIP_LONG_PRESS_MS = 500;
const TOUCH_TOOLTIP_MOVE_TOLERANCE_PX = 8;

@Component({
  selector: 'sm-action-button',
  templateUrl: './sm-action-button.component.html',
  styleUrls: ['./sm-action-button.component.scss'],
  standalone: false,
})
export class SmActionButtonComponent implements OnDestroy {
  @Input() variant: SmActionButtonVariant = 'primary';
  @Input() size: SmActionButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() busy = false;
  @Input() fullWidth = false;
  @Input() iconOnly = false;
  @Input() leadingIconName = '';
  @Input() trailingIconName = '';
  @Input() ariaLabel = '';
  @Input() ariaExpanded: boolean | null = null;
  @Input() ariaControls = '';
  @Input() title = '';
  @Input() overflowTooltipText = '';
  @Input() tooltipText = '';
  @ViewChild(SmOverflowTooltipComponent) private readonly overflowTooltip?: SmOverflowTooltipComponent;

  private longPressTimer: number | null = null;
  private longPressPointerId: number | null = null;
  private longPressStartX = 0;
  private longPressStartY = 0;
  private suppressNextClick = false;

  get isDisabled(): boolean {
    return this.disabled || this.busy;
  }

  handleTooltipOpen(): void {
    this.overflowTooltip?.showIfOverflowing();
  }

  handleTooltipClose(): void {
    this.overflowTooltip?.hideTooltip();
  }

  handleClick(event: MouseEvent): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.tooltipText) {
      this.handleTooltipClose();
      return;
    }

    this.handleTooltipOpen();
  }

  handlePointerDown(event: PointerEvent): void {
    if (!this.tooltipText || event.pointerType !== 'touch' || this.isDisabled) {
      return;
    }

    this.cancelLongPress();
    this.longPressPointerId = event.pointerId;
    this.longPressStartX = event.clientX;
    this.longPressStartY = event.clientY;
    this.longPressTimer = window.setTimeout(() => {
      this.longPressTimer = null;
      this.suppressNextClick = true;
      this.overflowTooltip?.showIfOverflowing();
    }, TOUCH_TOOLTIP_LONG_PRESS_MS);
  }

  handlePointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.longPressPointerId || this.longPressTimer === null) {
      return;
    }

    if (
      Math.abs(event.clientX - this.longPressStartX) > TOUCH_TOOLTIP_MOVE_TOLERANCE_PX
      || Math.abs(event.clientY - this.longPressStartY) > TOUCH_TOOLTIP_MOVE_TOLERANCE_PX
    ) {
      this.cancelLongPress();
    }
  }

  handlePointerEnd(event: PointerEvent): void {
    if (event.pointerId === this.longPressPointerId) {
      this.cancelLongPress();
    }
  }

  ngOnDestroy(): void {
    this.cancelLongPress();
  }

  get effectiveTooltipText(): string {
    return this.tooltipText || this.overflowTooltipText;
  }

  private cancelLongPress(): void {
    if (this.longPressTimer !== null) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    this.longPressPointerId = null;
  }
}
